import {
    Component,
    computed,
    DestroyRef,
    ElementRef,
    forwardRef,
    HostBinding,
    HostListener,
    inject,
    input,
    OnChanges,
    OnInit,
    output,
    signal,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ControlValueAccessor,
    FormControl,
    NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Icon } from '../../icon/icon.component';
import { DocumentService } from '../../../services/document.service';
import { computeConnectedPosition } from '../../../services/overlay-position';
import { dropdownFade } from './dropdown.animation';

/**
 * Representa uma Option do Dropdown.
 */
export type DropdownOption = {
    /** Texto exibido da opção. */
    label: string;
    /** Valor associado à opção. */
    value: any;
    /** Dados arbitrários associados à opção. */
    data?: any;
    /** Indica se a opção está marcada. */
    checked?: boolean | null;
    /** Classe de ícone FontAwesome (ex.: `fa-solid fa-bolt`). */
    icon?: string;
    /** URL de imagem usada como ícone da opção. */
    iconImage?: string;
    /** Quando verdadeiro, a opção fica esmaecida e não é selecionável. */
    disabled?: boolean;
};

/**
 * Contexto passado ao template customizado de opção (via `[optionTemplate]`),
 * usado tanto na lista de opções quanto no valor selecionado (gatilho).
 *
 * - `$implicit`/`option`: a opção sendo renderizada.
 * - `selected`: se a opção está selecionada.
 * - `active`: se a opção está com destaque de navegação por teclado (sempre
 *   `false` no gatilho).
 */
export type DropdownOptionContext = {
    $implicit: DropdownOption;
    option: DropdownOption;
    selected: boolean;
    active: boolean;
};

/** Lado preferido para abrir o painel de opções. */
export type DropdownOpenDirection = 'auto' | 'top' | 'bottom';

/**
 * Lista suspensa (single-select) integrável a formulários
 * (`ControlValueAccessor`), com **painel customizado** (não usa `<select>`
 * nativo), 100% temável.
 *
 * Recursos: ícones por opção, busca automática (quando há muitas opções),
 * navegação por teclado (setas/Enter/Esc/Home/End + type-ahead), limpar
 * seleção (clearable), opções desabilitadas, posicionamento que nunca abre
 * fora da tela, fechamento ao clicar fora / Esc e animação fade.
 */
@Component({
    selector: 'ai-dropdown',
    standalone: true,
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    imports: [NgClass, NgStyle, NgTemplateOutlet, Icon],
    encapsulation: ViewEncapsulation.Emulated,
    animations: [dropdownFade],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => Dropdown),
            multi: true,
        },
    ],
})
export class Dropdown implements OnInit, OnChanges, ControlValueAccessor {
    @HostBinding('attr.tabindex')
    tabindex = 0;

    @ViewChild('selection')
    protected selectionElement!: ElementRef<HTMLElement>;

    @ViewChild('optionBox')
    protected optionBoxElement?: ElementRef<HTMLElement>;

    // ---- Inputs (contrato preservado) ---------------------------------------

    /** Estilos inline aplicados ao gatilho. */
    readonly styleClasses = input<{}>({});

    /** Rótulo da opção "default" (não selecionada). */
    readonly defaultLabel = input<string>('Selecione');

    /** Se verdadeiro, volta à opção default ao recarregar as opções. */
    readonly returnToDefaultLabel = input<boolean>(false);

    /** Opções disponíveis. */
    readonly options = input<DropdownOption[]>([]);

    /**
     * Template opcional para customizar a renderização de cada opção. É usado
     * tanto na lista de opções quanto no valor selecionado (gatilho). O contexto
     * recebido é um {@link DropdownOptionContext}. Sem ele, é usado o formato
     * padrão (ícone da opção + rótulo).
     */
    readonly optionTemplate = input<TemplateRef<DropdownOptionContext> | null>(
        null,
    );

    // ---- Inputs novos (aditivos) --------------------------------------------

    /** Lado preferido para abrir o painel. */
    readonly openDirection = input<DropdownOpenDirection>('auto');

    /** A busca aparece quando o número de opções excede este limiar. */
    readonly searchThreshold = input<number>(8);

    /** Placeholder da caixa de busca. */
    readonly searchPlaceholder = input<string>('Pesquisar');

    /** Mensagem exibida quando a busca não retorna resultados. */
    readonly emptyText = input<string>('Nenhum resultado');

    /** Exibe o botão de limpar (volta à opção default) quando há seleção. */
    readonly clearable = input<boolean>(false);

    /** Desabilita o componente via binding `[disabled]`. */
    public readonly disabledInput = input<boolean>(false, {
        alias: 'disabled',
    });

    // ---- Output (contrato preservado) ---------------------------------------

    /** Emite a opção selecionada (ou `null`). */
    readonly selectionChange = output<DropdownOption | null>();

    // ---- Núcleo CVA (preservado) --------------------------------------------

    protected formControl = new FormControl<DropdownOption | null>(null, {
        nonNullable: true,
    });
    protected readonly defaultOption = computed(
        (): DropdownOption => ({
            value: '<DEFAULT>',
            label: this.defaultLabel(),
        }),
    );

    private onChanged: (value: DropdownOption | null) => void = () => {};
    private onTouched: () => void = () => {};

    // ---- Estado do painel ----------------------------------------------------

    private readonly cvaDisabled = signal<boolean>(false);
    /** Estado efetivo: desabilitado por input OU pelo Form. */
    public readonly disabled = computed(
        () => this.disabledInput() || this.cvaDisabled(),
    );

    protected readonly optionsVisible = signal<boolean>(false);
    protected readonly searchTerm = signal<string>('');
    /** Índice (em `filteredOptions`) do item ativo (teclado). */
    protected readonly activeIndex = signal<number>(-1);
    /** Valor selecionado, como signal reativo (espelha o formControl). */
    protected readonly selectedValue = signal<DropdownOption | null>(null);

    // Posição do painel (coordenadas de viewport — usadas com position: fixed).
    protected readonly panelTop = signal<number>(0);
    protected readonly panelLeft = signal<number>(0);
    protected readonly panelWidth = signal<number>(0);
    protected readonly panelMaxHeight = signal<number>(0);

    /** Opção realmente selecionada (não o sentinel default). */
    protected readonly selectedOption = computed((): DropdownOption | null => {
        const value = this.selectedValue();
        return value && value.value !== '<DEFAULT>' ? value : null;
    });

    /** Opções visíveis após o filtro de busca. */
    protected readonly filteredOptions = computed((): DropdownOption[] => {
        const term = this.normalize(this.searchTerm());
        if (!term) {
            return this.options();
        }
        return this.options().filter((option) =>
            this.normalize(option.label).includes(term),
        );
    });

    /** Indica se a caixa de busca deve ser exibida. */
    protected readonly showSearch = computed(
        () => this.options().length > this.searchThreshold(),
    );

    private readonly eRef = inject(ElementRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly documentService = inject(DocumentService);

    /** Handler de reposicionamento (scroll/resize) enquanto aberto. */
    private readonly repositionHandler = (): void => {
        if (this.optionsVisible()) {
            this.updatePanelPosition();
        }
    };

    /** Buffer de type-ahead e timestamp da última tecla. */
    private typeAheadBuffer = '';
    private typeAheadAt = 0;

    constructor() {
        this.documentService
            .onDocumentClick()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                if (
                    this.optionsVisible() &&
                    event &&
                    !this.eRef.nativeElement.contains(event.target)
                ) {
                    this.close();
                }
            });

        this.documentService
            .onKeyDownEscape()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.optionsVisible()) {
                    this.close();
                }
            });

        this.destroyRef.onDestroy(() => this.removeRepositionListeners());
    }

    // ---- Ciclo de vida (preservado) -----------------------------------------

    ngOnInit(): void {
        // valueChanges não completa sozinho; encerra com o componente.
        this.formControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value: DropdownOption | null) => {
                this.selectedValue.set(value);
                this.onChanged(value);
                this.selectionChange.emit(value);
            });
        this.selectedValue.set(this.formControl.value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['options']) {
            this.synchronizeLastWrittenValue();
            if (this.returnToDefaultLabel()) {
                this.formControl.setValue(this.defaultOption());
            }
        }
    }

    // ---- ControlValueAccessor (preservado) ----------------------------------

    registerOnChange(fn: any): void {
        this.onChanged = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.cvaDisabled.set(isDisabled);
        // `emitEvent: false`: habilitar/desabilitar não deve disparar
        // `valueChanges` (que chama `onChanged` e marcaria o form como dirty).
        isDisabled
            ? this.formControl.disable({ emitEvent: false })
            : this.formControl.enable({ emitEvent: false });
        if (isDisabled) {
            this.close();
        }
    }

    writeValue(value: DropdownOption | null): void {
        // `emitEvent: false`: escrita PROGRAMÁTICA não deve disparar o
        // `valueChanges` (que chama `onChanged`/`selectionChange`). Emitir aqui
        // faria o CVA marcar o controle pai como "dirty" na carga do formulário.
        // O valor exibido é atualizado por `synchronizeLastWrittenValue`.
        this.formControl.setValue(value, { emitEvent: false });
        this.synchronizeLastWrittenValue();
    }

    /** Sincroniza o estado com o último valor escrito (sentinel default). */
    private synchronizeLastWrittenValue(): void {
        const value: DropdownOption | null = this.formControl.value;

        if (!value) {
            this.formControl.setValue(this.defaultOption(), {
                emitEvent: false,
            });
            this.onChanged(this.defaultOption());
        }
        this.selectedValue.set(this.formControl.value);
    }

    // ---- Abertura/fechamento e posicionamento -------------------------------

    protected changeVisibility(): void {
        if (this.disabled()) {
            return;
        }
        this.optionsVisible() ? this.close() : this.open();
    }

    private open(): void {
        this.searchTerm.set('');
        this.optionsVisible.set(true);
        this.onTouched();
        // Posiciona o item ativo na opção selecionada (se houver).
        this.activeIndex.set(this.indexOfSelected());
        this.addRepositionListeners();
        requestAnimationFrame(() => this.updatePanelPosition());
    }

    private close(): void {
        this.optionsVisible.set(false);
        this.activeIndex.set(-1);
        this.removeRepositionListeners();
    }

    private addRepositionListeners(): void {
        window.addEventListener('scroll', this.repositionHandler, true);
        window.addEventListener('resize', this.repositionHandler);
    }

    private removeRepositionListeners(): void {
        window.removeEventListener('scroll', this.repositionHandler, true);
        window.removeEventListener('resize', this.repositionHandler);
    }

    private updatePanelPosition(): void {
        const trigger = this.selectionElement?.nativeElement;
        const panel = this.optionBoxElement?.nativeElement;
        if (!trigger || !panel) {
            return;
        }

        const rect = trigger.getBoundingClientRect();
        const position = computeConnectedPosition(
            {
                top: rect.top,
                bottom: rect.bottom,
                left: rect.left,
                width: rect.width,
            },
            { width: rect.width, height: panel.scrollHeight },
            { width: window.innerWidth, height: window.innerHeight },
            { preferred: this.openDirection() },
        );

        this.panelTop.set(position.top);
        this.panelLeft.set(position.left);
        this.panelWidth.set(position.width);
        this.panelMaxHeight.set(position.maxHeight);
    }

    // ---- Seleção -------------------------------------------------------------

    /** Seleciona uma opção (ignora as desabilitadas) e fecha o painel. */
    protected onSelectOption(option: DropdownOption): void {
        if (option.disabled) {
            return;
        }
        this.formControl.setValue(option);
        this.close();
    }

    /** Limpa a seleção, voltando à opção default. */
    protected clear(event: Event): void {
        event.stopPropagation();
        if (this.disabled()) {
            return;
        }
        this.formControl.setValue(this.defaultOption());
    }

    /** Verdadeiro quando a opção corresponde ao valor selecionado. */
    protected isSelected(option: DropdownOption): boolean {
        const value = this.selectedValue();
        return !!value && value.value === option.value;
    }

    // ---- Teclado -------------------------------------------------------------

    @HostListener('keydown', ['$event'])
    protected onKeydown(event: KeyboardEvent): void {
        if (this.disabled()) {
            return;
        }

        const key = event.key;
        const fromSearchField =
            (event.target as HTMLElement)?.tagName === 'INPUT';

        if (!this.optionsVisible()) {
            if (key === 'Enter' || key === ' ' || key === 'ArrowDown') {
                event.preventDefault();
                this.open();
            }
            return;
        }

        switch (key) {
            case 'Escape':
                event.preventDefault();
                this.close();
                return;
            case 'ArrowDown':
                event.preventDefault();
                this.moveActive(1);
                return;
            case 'ArrowUp':
                event.preventDefault();
                this.moveActive(-1);
                return;
            case 'Home':
                event.preventDefault();
                this.activeIndex.set(this.firstEnabledIndex(1));
                return;
            case 'End':
                event.preventDefault();
                this.activeIndex.set(this.firstEnabledIndex(-1));
                return;
            case 'Enter':
                event.preventDefault();
                this.selectActive();
                return;
            case ' ':
                if (fromSearchField) {
                    return; // espaço é digitação na busca
                }
                event.preventDefault();
                this.selectActive();
                return;
        }

        // Type-ahead: tecla imprimível localiza a opção por prefixo.
        if (!fromSearchField && key.length === 1 && !event.ctrlKey && !event.metaKey) {
            this.typeAhead(key);
        }
    }

    private selectActive(): void {
        const option = this.filteredOptions()[this.activeIndex()];
        if (option) {
            this.onSelectOption(option);
        }
    }

    private moveActive(delta: number): void {
        const options = this.filteredOptions();
        const total = options.length;
        if (total === 0) {
            this.activeIndex.set(-1);
            return;
        }
        let index = this.activeIndex();
        for (let step = 0; step < total; step++) {
            index = (index + delta + total) % total;
            if (!options[index].disabled) {
                this.activeIndex.set(index);
                return;
            }
        }
    }

    private firstEnabledIndex(direction: 1 | -1): number {
        const options = this.filteredOptions();
        if (direction === 1) {
            const i = options.findIndex((o) => !o.disabled);
            return i;
        }
        for (let i = options.length - 1; i >= 0; i--) {
            if (!options[i].disabled) {
                return i;
            }
        }
        return -1;
    }

    private indexOfSelected(): number {
        const value = this.selectedValue();
        if (!value) {
            return -1;
        }
        return this.filteredOptions().findIndex((o) => o.value === value.value);
    }

    private typeAhead(char: string): void {
        const now = Date.now();
        this.typeAheadBuffer = now - this.typeAheadAt > 600 ? char : this.typeAheadBuffer + char;
        this.typeAheadAt = now;

        const prefix = this.normalize(this.typeAheadBuffer);
        const options = this.filteredOptions();
        const index = options.findIndex(
            (o) => !o.disabled && this.normalize(o.label).startsWith(prefix),
        );
        if (index >= 0) {
            this.activeIndex.set(index);
        }
    }

    /** Atualiza o termo de busca e reposiciona o painel. */
    protected onSearchInput(value: string): void {
        this.searchTerm.set(value);
        this.activeIndex.set(this.firstEnabledIndex(1));
        requestAnimationFrame(() => this.updatePanelPosition());
    }

    /**
     * Combina marcas diacríticas (acentos) — faixa Unicode U+0300–U+036F.
     */
    private static readonly DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

    /** Normaliza texto para busca: minúsculas e sem acentos. */
    private normalize(text: string): string {
        return (text ?? '')
            .toLowerCase()
            .normalize('NFD')
            .replace(Dropdown.DIACRITICS, '');
    }
}
